import { LinearGradient } from 'expo-linear-gradient';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';

import { pointerEventsStyles, Button, HStack, Modal, theme } from '@sg/garnish';

export const AddressFormFooter = (props: AddressFormFooterProps) => {
  const { canSubmit, isLoading, onSubmit, canDelete, isDeleting, onDelete, submitCtaOverride } =
    props;

  const { formatMessage } = useIntl();

  return (
    <View style={styles.footerContainer}>
      <LinearGradient
        colors={FOOTER_GRADIENT_COLORS}
        locations={GRADIENT_LOCATIONS}
        style={[styles.footerGradient, pointerEventsStyles.none]}
      />

      <Modal.Footer style={styles.footer} withoutTopBorder>
        <HStack itemsPerRow={canDelete ? 2 : 1} gap={theme.spacing['4']}>
          {canDelete ? (
            <Button
              size="large"
              palette="caution"
              isLoading={isDeleting}
              onPress={onDelete}
              accessibilityLabel={formatMessage(messages.removeAddressA11yLabel)}
              accessibilityHint={formatMessage(messages.removeAddressA11yLabel)}
              accessibilityRole="button"
            >
              <FormattedMessage {...messages.removeAddressLabel} />
            </Button>
          ) : null}

          <Button
            size="large"
            isLoading={isLoading}
            onPress={onSubmit}
            disabled={!canSubmit}
            accessibilityLabel={formatMessage(messages.saveAddressA11yLabel)}
            accessibilityHint={formatMessage(messages.saveAddressA11yLabel)}
            accessibilityRole="button"
          >
            {submitCtaOverride ? (
              submitCtaOverride
            ) : (
              <FormattedMessage {...messages.saveAddressLabel} />
            )}
          </Button>
        </HStack>
      </Modal.Footer>
    </View>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────────

const messages = defineMessages({
  saveAddressLabel: {
    defaultMessage: 'Save',
    description: 'Address Form | Footer | Save',
  },
  saveAddressA11yLabel: {
    defaultMessage: 'Submit address form',
    description: 'Address Form | Footer | Save address a11y label',
  },
  removeAddressLabel: {
    defaultMessage: 'Remove',
    description: 'Address Form | Footer | Remove',
  },
  removeAddressA11yLabel: {
    defaultMessage: 'Remove address',
    description: 'Address Form | Footer | Remove address a11y label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const GRADIENT_HEIGHT = 24;
const GRADIENT_LOCATIONS = [0, 1] as const;
const FOOTER_GRADIENT_COLORS = [
  theme.colors.OPACITY.OATMEAL.TRANSPARENT,
  theme.colors.APP_BACKGROUND,
] as const;

// ─── Types ───────────────────────────────────────────────────────────────────

type AddressFormFooterProps = {
  canSubmit?: boolean;
  isLoading?: boolean;
  onSubmit: () => void;
  canDelete?: boolean;
  isDeleting?: boolean;
  onDelete?: () => void;
  submitCtaOverride?: string;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  footerContainer: {
    zIndex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
    marginTop: theme.spacing['3'],
    marginBottom: Platform.select({ android: theme.spacing['4'], default: 0 }),
  },
  footerGradient: {
    position: 'absolute',
    top: -(GRADIENT_HEIGHT + theme.spacing['3']),
    left: 0,
    right: 0,
    height: GRADIENT_HEIGHT,
  },
  footer: {
    flexDirection: 'row',
    backgroundColor: theme.colors.APP_BACKGROUND,
    padding: theme.spacing['6'],
    paddingTop: theme.spacing['4'],
  },
});
